import type { UseQueryResult } from '@tanstack/react-query'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import type { ReactNode } from 'react'

type QueryEntry<T> = {
  query: UseQueryResult<{ status: string; data: T }, Error>
  errorMessage: string
}

type SingleQueryProps<T, P> = {
  query: UseQueryResult<{ status: string; data: T; permissions?: P }, Error>
  errorMessage: string
  queries?: never
  children: (data: T, permissions: P) => ReactNode
}

type MultiQueryProps<T extends unknown[]> = {
  queries: { [K in keyof T]: QueryEntry<T[K]> }
  query?: never
  errorMessage?: never
  children: (data: T) => ReactNode
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-20">
      <LoadingSpinner size="lg" />
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-20">
      <ErrorBanner message={message} />
    </div>
  )
}

export function QueryGuard<T, P = undefined>(props: SingleQueryProps<T, P>): ReactNode
export function QueryGuard<T extends unknown[]>(props: MultiQueryProps<T>): ReactNode
export function QueryGuard(
  props: SingleQueryProps<unknown, unknown> | MultiQueryProps<unknown[]>
): ReactNode {
  if ('queries' in props && props.queries) {
    const { queries, children } = props as MultiQueryProps<unknown[]>

    for (const entry of queries) {
      if (entry.query.isLoading) return <LoadingState />
    }

    for (const entry of queries) {
      if (entry.query.error || !entry.query.data?.data) {
        return <ErrorState message={entry.query.error?.message || entry.errorMessage} />
      }
    }

    const data = queries.map((entry) => entry.query.data?.data) as unknown[]
    return children(data)
  }

  const { query, errorMessage, children } = props as SingleQueryProps<unknown, unknown>

  if (query.isLoading) return <LoadingState />

  if (query.error || !query.data?.data) {
    return <ErrorState message={query.error?.message || errorMessage} />
  }

  return children(query.data.data, query.data.permissions)
}
